/**
 * Analytics event constants — provider-agnostic.
 *
 * Naming convention:
 * - User actions: `act:event_name`
 * - System events: `evt:event_name`
 */

export const EVENTS = {
  // ===== REGISTRATION FLOW =====
  CLICK_REGISTER: "act:click_register",
  REGISTER_FORM_SUBMIT: "act:register_form_submit",
  OTP_REQUEST: "act:otp_request",
  OTP_REQUEST_RESULT: "evt:otp_request_result",
  OTP_VERIFY_RESULT: "evt:otp_verify_result",
  REGISTRATION_RESULT: "evt:registration_result",
  REGISTRATION_FORM_FOCUS: "act:registration_form_focus",
  GOOGLE_PROMPT_SHOWN: "evt:google_prompt_shown",
  GOOGLE_PROMPT_TAP: "act:google_prompt_tap",

  // ===== DEPOSIT FLOW =====
  CLICK_DEPOSIT: "act:click_deposit",
  SELECT_DEPOSIT_METHOD_GROUP: "act:select_deposit_method_group",
  SELECT_DEPOSIT_METHOD_INDIVIDUAL: "act:select_deposit_method_individual",
  SELECT_DEPOSIT_AMOUNT: "act:select_deposit_amount",
  DEFAULT_DEPOSIT_AMOUNT: "evt:default_deposit_amount",
  DEPOSIT_AMOUNT_CHANGED: "evt:deposit_amount_changed",
  CHANNEL_STEP_REACHED: "evt:channel_step_reached",
  TOP_UP_SUBMIT: "act:top_up_submit",
  DEPOSIT_CREATE_RESULT: "evt:deposit_create_result",
  AUTO_FAST_TRIGGERED: "evt:auto_fast_triggered",
  DEPOSIT_SUCCESS: "evt:deposit_success",
  DEPOSIT_FAILED: "evt:deposit_failed",
  CLOSE_DEPOSIT_IFRAME: "act:close_deposit_iframe",
  OPEN_DEPOSIT_IFRAME: "act:open_deposit_iframe",
  OPEN_DEPOSIT_CUSTOM: "act:open_deposit_custom",
  CLOSE_DEPOSIT_CUSTOM: "act:close_deposit_custom",

  // Empty states tracking (system events)
  DEPOSIT_GROUPS_EMPTY: "evt:deposit_groups_empty",
  DEPOSIT_METHODS_EMPTY: "evt:deposit_methods_empty",

  // ===== PGW REDIRECT AUTO-CLOSE =====
  REDIRECT_AUTOCLOSE_RESULT: "evt:redirect_autoclose_result",

  // ===== GAMEPLAY =====
  GAME_OPEN_TRIGGERED: "act:game_open_triggered",
  GAME_OPENED: "act:game_opened",
  GAME_CLOSED: "act:game_closed",
  GAME_EXIT: "act:game_exit",
  GAME_ERROR: "evt:game_error",
  SESSION_TIMEOUT: "evt:session_timeout",
  GAME_UNAVAILABLE: "evt:game_unavailable",
  GAME_SEARCH: "act:game_search",
  GAME_SEARCH_SELECT: "act:game_search_select",
  GAME_CONTENT_VISIBILITY_FALLBACK_CLICK: "act:game_content_visibility_fallback_click",

  // ===== SURPRISE ME =====
  // Prefixed per repo convention; the Notion specs write them bare, so experiment
  // 91420's metrics must be built against these values.
  SURPRISE_ME_TILE_SHOWN: "evt:surprise_me_tile_shown",
  SURPRISE_ME_TILE_CLICKED: "act:surprise_me_tile_clicked",
  SURPRISE_ME_PLAY_NOW: "act:surprise_me_play_now",
  SURPRISE_ME_NEXT: "act:surprise_me_next",
  SURPRISE_ME_BACK: "act:surprise_me_back",
  SURPRISE_ME_CLOSED: "act:surprise_me_closed",

  // ===== PROMOTIONS =====
  PROMO_CLAIM_ATTEMPT: "act:promo_claim_attempt",
  PROMO_CLAIM_SUCCESS: "evt:promo_claim_success",
  PROMO_ISSUED: "evt:promo_issued",
  PROMO_REWARD_APPLIED: "evt:promo_reward_applied",
  PROMO_BONUS_EXPIRED: "evt:promo_bonus_expired",
  CLICK_MINI_BANNER: "act:click-mini-banner",
  CLICK_HOME_BANNER: "act:click-home-banner",
  CLICK_PROMOTION_PAGE: "act:click-promotion-page",
  CLICK_PROMOTION_ITEM: "act:click-promotion-item",
  CASHBACK_MINI_PROMPT_SHOWN: "evt:cashback-mini-prompt-shown",
  PROMO_AUTO_CLAIM_POPUP: "evt:promo_auto_claim_popup",

  // Canonical promotion journey events. Run alongside the legacy events above
  // until dashboards move to the new contract.
  PROMOTION_PRESENTED: "evt:promotion_presented",
  PROMOTION_ACTION: "act:promotion_action",
  PROMOTION_CLAIM_RESULT: "evt:promotion_claim_result",
  PROMOTION_ISSUED: "evt:promotion_issued",
  PROMOTION_QUEUE_ENDED: "evt:promotion_queue_ended",
  NUDGE_MODAL_PRESENTED: "evt:nudge_modal_presented",

  // ===== WITHDRAWALS =====
  CLICK_WITHDRAWAL: "act:click_withdrawal",
  WITHDRAWAL_SUBMIT: "act:withdrawal_submit",
  WITHDRAWAL_SUCCESS: "evt:withdrawal_success",
  WITHDRAWAL_REQUEST_FAILED: "evt:withdrawal_request_failed",

  // ===== NOTIFICATIONS =====
  NOTIFICATION_INAPP_PROMPT_SHOWN: "evt:notification_inapp_prompt_shown",
  NOTIFICATION_INAPP_PROMPT_RESPONSE: "act:notification_inapp_prompt_response",
  NOTIFICATION_BROWSER_PROMPT_SHOWN: "evt:notification_browser_prompt_shown",
  NOTIFICATION_BROWSER_PROMPT_RESPONSE: "act:notification_browser_prompt_response",
  NOTIFICATION_SUBSCRIPTION_STATUS: "evt:notification_subscription_status",

  // ===== POPUPS / OVERLAYS =====
  POPUP_SHOWN: "evt:popup_shown",

  // ===== LANGUAGE =====
  CHANGE_LANGUAGE: "act:change_language",

  // ===== CUSTOMER SUPPORT (CS) =====
  CLICK_CS: "act:click_cs",
  SELECT_CS_CHANNEL: "act:select_cs_channel",
  CONTACT_CS_ENTRY_SHOWN: "evt:contact_cs_entry_shown",

  // ===== HEADER =====
  HEADER_SHOWN: "evt:header_shown",

  // ===== PROMOTION USER JOURNEY =====
  SHARE_CONFIRMED: "evt:share_confirmed",
  REFERRAL_LINK_OPENED: "evt:referral_link_opened",
  REFER_BALLS_LAST_BALL_USED: "evt:refer_balls_last_ball_used",

  // ===== LEGACY/GENERAL =====
  CLICK_LOGIN: "act:click_login",
  CLICK_GOOGLE_AUTH: "act:click_google_auth",
  LOGIN_FORM_SUBMIT: "act:login_form_submit",
  LOGIN: "act:login",
  LOGIN_FAILED: "evt:login_failed",
  LOGOUT: "act:logout",
  UPDATE_PROFILE: "act:update_profile",
  PAGE_VIEW: "act:page_view",
  SESSION_STARTED: "evt:session_started",
  SESSION_ENDED: "evt:session_ended",
  API_ERROR: "evt:api_error",

  // ===== PWA INSTALL FUNNEL =====
  PWA_ENTRY_SHOWN: "evt:pwa_entry_shown",
  PWA_INSTALL_CLICK: "evt:pwa_install_click",
  PWA_INSTALL_SUCCESS: "evt:pwa_install_success",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

// Game event sources
export const GAME_SOURCE = {
  MAIN_LIST: "main_list",
  LAST_PLAYED: "last_played",
  SEARCH: "search",
  PROVIDER: "provider",
  PROMO_BANNER: "promo_banner",
  SURPRISE_ME: "surprise_me",
  OTHER: "other",
} as const;

export type GameSource = (typeof GAME_SOURCE)[keyof typeof GAME_SOURCE];

// Redirect auto-close surfaces. Only one value in use today; reserved for
// other self-closing redirect flows (e.g. Google auth popup) later.
export const REDIRECT_SURFACE = {
  DEPOSIT_PGW_RETURN: "deposit_pgw_return",
} as const;

export type RedirectSurface = (typeof REDIRECT_SURFACE)[keyof typeof REDIRECT_SURFACE];

// The `statsig:` prefix is legacy. Kept as-is: renaming would re-fire the event
// once for every session already mid-flight at deploy time.
export const REGISTRATION_FORM_FOCUS_SESSION_KEY = "statsig:registration_form_focus_fired";
export const FIRST_DEPOSIT_SESSION_KEY = "analytics:first_deposit_tracked";
